/* eslint-disable @typescript-eslint/no-duplicate-enum-values, @typescript-eslint/no-explicit-any */

import { AlertColor, SelectChangeEvent } from '@mui/material';
import { ReactNode } from 'react';

export enum DashboardLayoutType {
  RESIZABLE_STANDARD = 'resizable_standard',
  RESIZABLE_VERTICAL = 'resizable_vertical',
}

export enum OptionType {
  PUT = 'put',
  CALL = 'call',
}

export enum HiroOptionType {
  TOT = 'TOT',
  P = 'P',
  C = 'C',
}

export enum ProductType {
  HIRO = 'hiro',
  EQUITYHUB = 'equityhub',
  SCANNERS = 'scanners',
  INDICES = 'indices',
  FOUNDERS_NOTES = 'founders_notes',
  REPORTS = 'reports',
  HOME = 'home',
  TRAINING = 'training',
  INTEGRATIONS = 'integrations',
  IMPLIED_VOL = 'ivol',
  TAPE = 'tape',
  INTERNAL_OPEN_INTEREST = 'oi',
  INTERNAL_RISK_ENGINE = 'risk',
  OPTIONS_CALCULATOR = 'optionsCalculator',
  TRACE = 'trace',
  CANVAS = 'canvas',
}

export enum ProductGroup {
  MARKET_CENTRAL = 'Market Central',
  EQUITY_CONSOLE = 'Equity Console',
  REAL_TIME_SIGNALS = 'Real Time Signals',
  INTERNAL = 'Internal',
}

export interface TabItem {
  label: string;
  value: string;
  content: ReactNode;
  actions?: ReactNode[];
}

export type Label = { name: string; description?: string; icon?: JSX.Element };

export type Level = {
  field: string;
  value: number;
  name: string;
};

export type Bounds = {
  left: number | string;
  right: number | string;
  leftIdx?: number;
  rightIdx?: number;
};

export type ChartZoomConfig = Bounds & {
  data: any[] | undefined;
  refAreaLeft: number | string;
  refAreaRight: number | string;
  top: number | string;
  bottom: number | string;
};

export type BiaxialChartZoomConfig = ChartZoomConfig & {
  top2: number | string;
  bottom2: number | string;
};

export type BrushZoomConfig = {
  data: any[] | undefined;
  leftIdx: number | undefined;
  refAreaLeft: any;
  rightIdx: number | undefined;
  refAreaRight: any;
};

export enum ToastType {
  SUCCESS = 'success',
}

export type ToastData = {
  message: string;
  type: AlertColor;
  duration?: number;
};

export interface EconomicCalendarData {
  event: string;
  date: string;
  country: string;
  currency: string | null;
  actual: number | null;
  previous: number | null;
  change: number | null;
  changePercentage: number | null;
  estimate: any;
  impact: string | null;
}

export interface EconomicCalendarData {
  event: string;
  date: string;
  country: string;
  currency: string | null;
  actual: number | null;
  previous: number | null;
  change: number | null;
  changePercentage: number | null;
  estimate: any;
  impact: string | null;
}

export interface SuggestionSearchData {
  symbol: string;
  name: string;
}

export type SearchHandlerFunction = (value: string) => void | Promise<void>;

export type TimezoneOption = {
  label: string;
  value: string;
};

export enum LinkGroupId {
  Resources = 'Resources',
  Social = 'Social',
  Account = 'Account',
}

export enum StreamType {
  AbsolutePrice = 0x4,

  FILTERED_DELTA = 0x00_00_01,
  ABS_DELTA = 0x00_00_02,
  PRICES = 0x00_00_04,

  // All
  FilteredTotalDelta = 0x08,
  AbsoluteTotalDelta = 0x10,
  FilteredCallDelta = 0x20,
  FilteredPutDelta = 0x40,

  // NextExp
  FilteredNextExpTotalDelta = 0x80,
  AbsoluteNextExpTotalDelta = 0x1_00,
  FilteredNextExpCallDelta = 0x2_00,
  FilteredNextExpPutDelta = 0x4_00,

  // Retail
  FilteredRetailTotalDelta = 0x8_00,
  AbsoluteRetailTotalDelta = 0x10_00,
  FilteredRetailCallDelta = 0x20_00,
  FilteredRetailPutDelta = 0x40_00,

  // Volume
  UNDERLYING_VOLUME = 0x0_80_00,

  // Full absolute signal (price, bid, ask, & ivol)
  FULL_ABSOLUTE_SIGNAL = 0x1_00_00,
}

export enum QuadrantTabCategory {
  CHART = 'charts',
  TABLE = 'tables',
  TEXT_CONTENT = 'articles',
  LIST = 'lists',
}

export interface QuadrantTab {
  id: string;
  contentId: string;
  label: string;
  category: QuadrantTabCategory;
  icon?: JSX.Element;
  sym?: string;
  symOptions?: string[];
}

export enum QuadrantId {
  TOP_LEFT = '1',
  TOP_RIGHT = '2',
  BOTTOM_LEFT = '3',
  BOTTOM_RIGHT = '4',
}

export interface Quadrant {
  tabs: QuadrantTab[];
}

export type QuadrantRowAction = 'add' | 'remove';

export type SymSelectorSettings = {
  value: string;
  options: string[];
  loading?: boolean;
  onChange: (event: SelectChangeEvent) => void;
};

export enum DataExchangeAgreement {
  INSTITUTIONAL_TRADER = 'institutional_trader',
  NON_BUSINESS_DATA_USAGE = 'data_usage_non_business',
  REGISTERED_BODY = 'is_registered_body',
  ASSET_MANAGER = 'is_asset_manager',
  EXTERNAL_CAPITAL_USE = 'external_capital_usage',
  TRADING_FOR_CORP = 'trading_for_corp',
  IS_PROFIT_SHARING = 'is_profit_sharing',
  BENEFIT_EXCHANGE_FOR_TRADING = 'benefit_exchange_for_trading',
}

export type DataExchangeAgreementMap = Record<
  DataExchangeAgreement,
  boolean | null
>;

export type CustomGridController = {
  elementId: string;
  component: JSX.Element;
};

export interface Earnings {
  companyName: string;
  day: string;
  utc: string;
  sym: string;
  pv: number;
  period: string;
  inHiro: boolean;
  impliedMove: number;
  cv: number;
  confirmed: number;
  activityFactor: number;
}

export interface FetchAuxOptions extends RequestInit {
  local?: boolean;
  maxRetries?: number;
  retryCounter?: number;
  buffer?: boolean;
  host?: string;
  headers?: any;
}

export type Gamma = {
  call_gnot: number;
  put_gnot: number;
  price: number;
};

export interface ComboGroup {
  combo: string;
  shared: number;
  symbols: string[];
  price_sym: string;
}

export interface Manifest {
  combos: ComboGroup[];
  combosSet: Set<string>;
  underlyingsSet: Set<string>;
}

export type ErrorResponse = {
  error: string;
};

type PollAuxResponse = { status: number; headers: any };
export type PollJsonResponse = { json: any } & PollAuxResponse;
export type PollBufferResponse = { data: any } & PollAuxResponse;

export interface PopperInfo {
  label?: string;
  description: string;
}

export type ValueFormat = 'currency' | 'date' | 'number';

export interface Candle {
  close: number;
  datetime: string;
  high: number;
  low: number;
  open: number;
  volume: number;
}

export interface DailyCandlesData {
  meta: {
    symbol: string;
    interval: string;
    exchange_timezone: string;
  };
  values?: Candle[];
}

export type CandlesData = {
  [sym: string]: DailyCandlesData;
};

/// used to create "nominal" type aliases for primatives so that different
/// semantic _kinds_ of primitive data cannot be assigned to other semantically
/// different _kinds_ of primitive data.
///
/// A Symbol may be stored as a string and a DateTime may be stored as a string
/// but you should never be able to accidentally assign a DateTime string to a
/// Symbol.
///
/// This generic type creates type checks to disallow such assignment, but
/// accomplishes this with full type-erasure at runtime
///
/// https://stackoverflow.com/a/50528847/20712
export type Opaque<T, K> = T & { __opaque__: K };

/// the string name of a trading symbol
export type Sym = Opaque<string, 'Sym'>;

// a `Date()` parseable string
export type DateTimeStr = Opaque<string, 'DateTimeStr'>;

/// The number of milliseconds after the Unix EPOCH - 1970-01-01 00:00:00 UTC
export type TimestampMillis = Opaque<number, 'TimstampMillis'>;

/// The number of seconds after the Unix EPOCH - 1970-01-01 00:00:00 UTC
export type TimestampSeconds = Opaque<number, 'TimestampSeconds'>;

export type EtDate = Opaque<Date, 'EtDate'>;
